import { useRef } from "react";
import { Ban, Check, Pipette } from "lucide-react";
import { cn } from "@/lib/utils";
import { contrastTextColor, isValidTagColor } from "@/utils/color.utils";

/**
 * Preset tag colors (Trello-style label palette). The custom swatch at the
 * end lets the user pick any other #RRGGBB via the native color input.
 */
export const TAG_COLOR_PRESETS: { value: string; label: string }[] = [
	{ value: "#61bd4f", label: "Grön" },
	{ value: "#f2d600", label: "Gul" },
	{ value: "#ff9f1a", label: "Orange" },
	{ value: "#eb5a46", label: "Röd" },
	{ value: "#c377e0", label: "Lila" },
	{ value: "#0079bf", label: "Blå" },
	{ value: "#00c2e0", label: "Turkos" },
	{ value: "#51e898", label: "Lime" },
	{ value: "#ff78cb", label: "Rosa" },
	{ value: "#344563", label: "Mörkblå" },
];

interface TagColorPickerProps {
	/** Current #RRGGBB value, or undefined for "no color". */
	value?: string;
	onChange: (color: string | undefined) => void;
	disabled?: boolean;
}

const swatchClasses =
	"flex h-8 w-8 items-center justify-center rounded border transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50";

/**
 * Swatch-row color picker for tag types: "no color", the preset palette and
 * a custom swatch backed by the native color input.
 */
const TagColorPicker = ({ value, onChange, disabled }: TagColorPickerProps) => {
	const customInputRef = useRef<HTMLInputElement>(null);

	const isCustom = !!value && !TAG_COLOR_PRESETS.some((preset) => preset.value === value);

	return (
		<div>
			<p className="mb-1.5 text-sm font-semibold text-gray-700">Färg</p>
			<div className="flex flex-wrap items-center gap-1.5">
				<button
					type="button"
					disabled={disabled}
					onClick={() => onChange(undefined)}
					aria-label="Ingen färg"
					title="Ingen färg"
					className={cn(
						swatchClasses,
						"border-gray-300 bg-white text-gray-400 hover:bg-gray-50",
						!value && "ring-2 ring-gray-900 ring-offset-1"
					)}
				>
					<Ban size={15} />
				</button>

				{TAG_COLOR_PRESETS.map((preset) => {
					const selected = value === preset.value;
					return (
						<button
							key={preset.value}
							type="button"
							disabled={disabled}
							onClick={() => onChange(preset.value)}
							aria-label={preset.label}
							title={preset.label}
							className={cn(
								swatchClasses,
								"border-black/10",
								selected && "ring-2 ring-gray-900 ring-offset-1"
							)}
							style={{ backgroundColor: preset.value }}
						>
							{selected && <Check size={15} color={contrastTextColor(preset.value)} />}
						</button>
					);
				})}

				<button
					type="button"
					disabled={disabled}
					onClick={() => customInputRef.current?.click()}
					aria-label="Egen färg"
					title="Egen färg"
					className={cn(
						swatchClasses,
						"relative border-black/10",
						isCustom && "ring-2 ring-gray-900 ring-offset-1"
					)}
					style={
						isCustom
							? { backgroundColor: value }
							: {
									background:
										"conic-gradient(#eb5a46, #f2d600, #61bd4f, #00c2e0, #c377e0, #eb5a46)",
							  }
					}
				>
					{isCustom ? (
						<Check size={15} color={contrastTextColor(value!)} />
					) : (
						<Pipette size={13} color="#ffffff" />
					)}
					<input
						ref={customInputRef}
						type="color"
						tabIndex={-1}
						value={isCustom ? value : "#61bd4f"}
						onChange={(e) => {
							const color = e.target.value.toLowerCase();
							if (isValidTagColor(color)) onChange(color);
						}}
						className="pointer-events-none absolute h-0 w-0 opacity-0"
					/>
				</button>
			</div>
		</div>
	);
};

export default TagColorPicker;
